/**
 * Consentable services — the ONLY place that knows which optional, consent-
 * requiring integrations the shop software can run.
 *
 * Three consumers read this registry (via registry.ts):
 *   - ConsentManager.tsx  — shows the cookie banner only when a shop has at
 *                           least one ACTIVE entry, and wires accept/reject;
 *   - useConsent.ts       — lets React code ask `isAccepted('<id>')`;
 *   - pages/CookieInfo.tsx — lists the active entries on /cookies.
 * Because all three read the same list, the banner, the preferences modal
 * and the information page can never disagree about what the shop uses.
 *
 * HOW TO ADD A SERVICE (the recipe):
 *   1. Give it a stable `id` (also the i18n label key `consent.services.<id>`
 *      — add that key in en/hu/de) and a `revisionBit` that is a power of two
 *      no other entry uses, ever (bits are never reused: the OR of the active
 *      bits is the library's `revision`, and a reused bit would fail to
 *      re-prompt returning visitors).
 *   2. `isEnabledFor` reads the shop's settings from /accounts/info. It must be
 *      a pure predicate: no side effects, no globals.
 *   3. `onAccept` does the real work (load the script, send the consent
 *      event). `onReject` must leave the site fully functional and undo what
 *      it can. Both may be called more than once per page load.
 *   4. List the cookies the service sets in `cookies`; the library deletes
 *      them on reject.
 *   5. Add the service's row(s) to thirdPartyInventory.ts / storageInventory.ts
 *      so /cookies stays truthful. Nothing else needs touching.
 *
 * Non-goals (deliberately not built): Google Consent Mode signalling, Global
 * Privacy Control, server-side consent logging, CCPA/regional variants.
 * See docs/CONSENT.md.
 */
import type { AccountInfo } from '../api/types';

export type ConsentCategory = 'analytics' | 'marketing';

export interface ConsentableService {
  /** Stable id; doubles as the library's service key and the i18n label key suffix. */
  id: string;
  category: ConsentCategory;
  /** Power of two, unique across all entries that ever existed. */
  revisionBit: number;
  /** Pure predicate over the shop's public settings. */
  isEnabledFor(accountInfo: AccountInfo): boolean;
  onAccept(): void;
  onReject(): void;
  /** Cookies the service sets; auto-cleared by the library on reject. */
  cookies?: ReadonlyArray<{ name: string | RegExp }>;
}

/**
 * Production registry. Empty today: no shop runs a consent-requiring service,
 * so no shop shows a banner. The two commented entries below are the worked
 * examples for the shapes we expect first.
 */
export const CONSENTABLE_SERVICES: readonly ConsentableService[] = [
  // --- Example 1: script that is ALREADY loaded and needs a consent signal --
  // The Full Barion Pixel (docs.barion.com "Barion Pixel Consent Management
  // requirements"): Barion requires `bp('consent', 'grantConsent')` before it
  // approves a Full Pixel integration, and `bp('consent', 'rejectConsent')`
  // on refusal. The BASE pixel keeps running either way — Barion mandates it
  // under legitimate interest for fraud prevention. The Base Pixel is injected
  // server-side by the HTML shell (webclient/shell/barionPixel.ts) and stays
  // Base-only on purpose. A future `setting.barion_full_pixel` flag would gate
  // this entry.
  //
  // {
  //   id: 'barion_full_pixel',
  //   category: 'marketing',
  //   revisionBit: 1 << 0,
  //   isEnabledFor: (info) => !!info.setting?.barion_pixel_id && !!info.setting?.barion_full_pixel,
  //   onAccept: () => sendBarionConsent('grantConsent'),   // new client-side module; the shell's Base snippet stays init-only
  //   onReject: () => sendBarionConsent('rejectConsent'),
  //   cookies: [{ name: /^ba_/ }],
  // },
  //
  // --- Example 2: plain tracker loaded only after consent ------------------
  // Google Analytics 4. A future `setting.google_analytics_id` would gate it.
  //
  // {
  //   id: 'google_analytics',
  //   category: 'analytics',
  //   revisionBit: 1 << 1,
  //   isEnabledFor: (info) => !!info.setting?.google_analytics_id,
  //   onAccept: () => loadGtag(readMeasurementId()),      // injects the gtag <script>
  //   onReject: () => { /* nothing loaded; library clears the cookies */ },
  //   cookies: [{ name: /^_ga/ }, { name: '_gid' }],
  // },
];
